import { useCallback, useContext, useEffect, useMemo } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { APPLE_PAY_SDK_URL } from '@/constants/nuvei';
import {
  fetchNuveiAppleMerchantSession,
  isNuveiPaymentSessionFresh,
  loadScriptOnce,
  resolveApplePaySessionVersion,
} from '@/utils/nuvei';
import { getApplePaySessionConstructor, type ApplePaySessionLike } from '@/utils/applePay';
import { getNuveiApplePayPaymentRequestAmount, isNuveiApplePayPaymentRequestAmountSupported } from '@/utils/pricing';
import { logError, logInfo } from '@/utils/logger';
import {
  classifyApplePayCancel,
  getGroundTruthTopLevelDomain,
  installApplePayMerchantValidationProbe,
  isApplePayDomainFailure,
  resetApplePayCancelDiagnostics,
} from '@/utils/applePayCancelDiagnostics';
import { markApplePayDomainFailure, armApplePayTabFallbackIfEligible } from '@/utils/applePayTabFallback';
import { isEmailValid } from '@/utils/email';
import { shouldCollectZipCodeForTaxCalculation } from '@/utils/tax';
import { WalletButtonShell } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { ApplePayButtonPlaceholder } from '@/components/Wallets/WalletButton/ApplePayButtonPlaceholder';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import type { NuveiApplePayPaymentParams } from '@/types/paymentHandlers';
import type { ExpressCheckoutComponentProps } from '@/types/expressCheckout';
import { isNuveiPendingResponse, isNuveiSuccessResponse } from '@/types/guards';
import { useNuveiWalletSettings } from '@/hooks/useNuveiWalletSettings';
import { useNuveiWalletLifecycle } from '@/hooks/useNuveiWalletLifecycle';
import { useLatestRef } from '@/hooks/useLatestRef';

type NuveiApplePayFormProps = ExpressCheckoutComponentProps;

type ApplePaySessionData = {
  sessionToken: string;
};

/**
 * Nuvei Apple Pay express button.
 *
 * Merchant validation uses the native Apple Pay session because checkout runs
 * in a cross-origin iframe. The encrypted Apple token is charged server-side
 * through Nuvei's signed /payment.do API.
 */
export const NuveiApplePayForm = ({ onInvalidTaxClick, onReady, isExpress }: NuveiApplePayFormProps) => {
  const context = useContext(CheckoutContext);
  const {
    paymentKeys,
    paymentMethodsSettings,
    pricing,
    customer,
    addressDetails,
    featureFlags,
    actions: { createSession, runPayment, setError, setIsExpressPaymentInProcess },
  } = context;

  const { providerSettings, nuveiConfig } = useNuveiWalletSettings(
    PaymentMethodEnum.APPLE_PAY,
    paymentKeys,
    paymentMethodsSettings
  );

  const providerSettingsRef = useLatestRef(providerSettings);
  const nuveiConfigRef = useLatestRef(nuveiConfig);
  const createSessionRef = useLatestRef(createSession);
  const customerRef = useLatestRef(customer);
  const pricingRef = useLatestRef(pricing);
  const addressDetailsRef = useLatestRef(addressDetails);
  const sessionBindingKey = JSON.stringify([pricing.priceBreakdown.priceTotal, pricing.currencyCode]);
  const sessionBindingKeyRef = useLatestRef(sessionBindingKey);
  // Session creation requires a valid `receipt_email` (the gateway 400s on a
  // partial/missing one). A missing email must NOT block the wallet: prefetch
  // is deferred (via `canCreateSession`) and the click opens the sheet, which
  // collects the buyer's email itself — Adyen/Stripe parity.
  const hasValidCustomerEmail = isEmailValid(customer.email);
  const hasValidCustomerEmailRef = useLatestRef(hasValidCustomerEmail);
  const isPaymentRequestSupported = useMemo(
    () => isNuveiApplePayPaymentRequestAmountSupported(pricing.priceBreakdown.priceTotal, pricing),
    [pricing]
  );
  const contextRef = useLatestRef(context);
  const runPaymentRef = useLatestRef(runPayment);
  const setErrorRef = useLatestRef(setError);
  const setIsExpressPaymentInProcessRef = useLatestRef(setIsExpressPaymentInProcess);
  const onInvalidTaxClickRef = useLatestRef(onInvalidTaxClick);

  const fetchSession = useCallback(async (): Promise<ApplePaySessionData | null> => {
    const activeProvider = providerSettingsRef.current;
    if (!activeProvider) return null;
    const sessionResponse = await createSessionRef.current({
      paymentMethodId: activeProvider.id || '',
      email: customerRef.current.email || '',
    });
    return { sessionToken: sessionResponse.sessionToken };
  }, [createSessionRef, customerRef, providerSettingsRef]);

  const {
    state,
    setState,
    prefetchRef,
    prefetchSession,
    refreshPaymentReadiness,
    isPaymentReady,
    buttonState,
    isPayingRef,
    settleAvailability,
    revealWallet,
  } = useNuveiWalletLifecycle({
    wallet: 'apple_pay',
    bindingKey: sessionBindingKey,
    fetchSession,
    prefetchErrorMessage: 'Failed to prefetch Nuvei Apple Pay session',
    isPaymentRequestSupported,
    canCreateSession: hasValidCustomerEmail,
    onReady,
  });

  useEffect(() => {
    // Wait for routed config — do not settle unavailable yet (one-shot
    // settle would lock the wallet out when config arrives on a later pass).
    if (!providerSettings || !nuveiConfig) {
      setState('loading');
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        await loadScriptOnce(APPLE_PAY_SDK_URL, 'apple-pay-sdk').catch(() => undefined);

        const ApplePaySessionCtor = getApplePaySessionConstructor();
        if (!ApplePaySessionCtor?.canMakePayments()) {
          if (!cancelled) {
            // Framed iOS with an unverified/blocked parent domain (or no
            // ApplePaySession): keep Apple Pay via the new-tab fallback.
            if (armApplePayTabFallbackIfEligible(featureFlags)) return;
            setState('unavailable');
            settleAvailability(false, 'device_unsupported');
          }
          return;
        }

        // Snapshot the merchant session domain at validation time so a later
        // CANCEL can be classified (PassKit rejection vs domain failure vs real
        // user cancel). Same probe the Adyen Apple Pay flow installs.
        installApplePayMerchantValidationProbe();

        const merchantIdentifier = nuveiConfig.merchantIdentifier;
        if (merchantIdentifier && ApplePaySessionCtor.canMakePaymentsWithActiveCard) {
          const canPay = await ApplePaySessionCtor.canMakePaymentsWithActiveCard(merchantIdentifier);
          if (!canPay) {
            if (!cancelled) {
              if (armApplePayTabFallbackIfEligible(featureFlags)) return;
              setState('unavailable');
              settleAvailability(false, 'unavailable');
            }
            return;
          }
        }

        // Without a valid email the session prefetch is deferred; the button
        // still settles ready AND stays clickable — the Apple Pay sheet
        // collects the email (shipping contact) and the session is created in
        // `onpaymentauthorized` (see onPay).
        if (hasValidCustomerEmailRef.current) {
          const prefetched = await prefetchSession();
          if (!prefetched) {
            throw new Error('Unable to prefetch Apple Pay session');
          }
        }

        if (!cancelled) {
          setState('ready');
          settleAvailability(true);
          revealWallet();
        }
      } catch (error) {
        logError('Failed to init Nuvei Apple Pay', error);
        if (!cancelled) {
          setState('unavailable');
          settleAvailability(false, 'load_error');
        }
      }
    };

    void init();
    return () => {
      cancelled = true;
    };
    // Email changes must NOT re-init (that unmounts wallets) — the lifecycle's
    // `canCreateSession` resumes the session prefetch once an email is valid.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once; unstable context objects must not re-init Apple Pay
  }, [
    providerSettings?.id,
    nuveiConfig?.merchantId,
    nuveiConfig?.merchantSiteId,
    settleAvailability,
    prefetchSession,
    revealWallet,
  ]);

  const finishPayAttempt = useCallback(
    (opts?: { clearExpressLoader?: boolean; refreshSession?: boolean }) => {
      isPayingRef.current = false;
      if (opts?.clearExpressLoader !== false) {
        setIsExpressPaymentInProcessRef.current(false);
      }
      if (opts?.refreshSession !== false) {
        void refreshPaymentReadiness();
      }
    },
    [isPayingRef, refreshPaymentReadiness, setIsExpressPaymentInProcessRef]
  );

  const onPay = useCallback(() => {
    const activeProvider = providerSettingsRef.current;
    const activeNuvei = nuveiConfigRef.current;
    const ApplePaySessionCtor = getApplePaySessionConstructor();
    if (!activeProvider || !activeNuvei || !ApplePaySessionCtor || isPayingRef.current) {
      return;
    }

    // Nuvei does not collect ZIP in-sheet yet — always use on-page ZIP gate
    // (even when WALLET_ZIP_TAX is on; Adyen/Stripe handle in-sheet separately).
    const needsZip = shouldCollectZipCodeForTaxCalculation(
      customerRef.current,
      addressDetailsRef.current?.zipCode ?? customerRef.current.address?.zipCode,
      pricingRef.current.priceBreakdown.isTaxValid
    );
    if (needsZip) {
      onInvalidTaxClickRef.current?.();
      return;
    }

    const candidate = prefetchRef.current;
    const prefetched =
      candidate &&
      candidate.bindingKey === sessionBindingKeyRef.current &&
      isNuveiPaymentSessionFresh(candidate.createdAt)
        ? candidate
        : null;
    if (!prefetched && hasValidCustomerEmailRef.current) {
      // With a valid checkout email the session is prefetched out of band; the
      // disabled button blocks clicks until the refresh lands.
      void refreshPaymentReadiness();
      return;
    }
    // No valid checkout email → no session could exist yet. Open the sheet
    // anyway: it collects the buyer's email (shipping contact) and the session
    // is created inside `onpaymentauthorized` — the same sheet-first order the
    // Adyen/Stripe wallet flows use. Merchant validation does not need it.

    let amount: string;
    try {
      amount = getNuveiApplePayPaymentRequestAmount(pricingRef.current.priceBreakdown.priceTotal, pricingRef.current);
    } catch (error) {
      logError('Nuvei Apple Pay amount has unsupported precision', error);
      setErrorRef.current('Apple Pay is unavailable for this amount.');
      setState('unavailable');
      settleAvailability(false, 'unavailable');
      return;
    }

    isPayingRef.current = true;
    setState('loading');
    prefetchRef.current = null;
    // Fresh sheet: drop any snapshot from a previous attempt so a stale value
    // can't misclassify this cancel.
    resetApplePayCancelDiagnostics();
    // Do NOT set the express loader here — a full-screen overlay during the
    // Apple Pay sheet can tear the session down after merchant validation
    // (oncancel / Payment Not Completed) before onpaymentauthorized.
    // Loader starts in onpaymentauthorized when we actually charge.
    trackAndNotifyPaymentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.APPLE_PAY });

    // Apple: country where purchase is processed (merchant), not shopper geo.
    const countryCode = (
      activeNuvei.countryCode ||
      customerRef.current.geoLocator?.countryCode2 ||
      customerRef.current.taxCountry ||
      'US'
    ).toUpperCase();
    const currencyCode = pricingRef.current.currencyCode.toUpperCase();
    const totalLabel = activeProvider.paymentMethodDisplayName || 'Store';

    const version = resolveApplePaySessionVersion(ApplePaySessionCtor);
    let session: ApplePaySessionLike;
    try {
      // Match SafeCharge buildSession defaults (safecharge.js): supports3DS +
      // credit/debit. supports3DS alone is Apple-minimum but Nuvei's Web SDK
      // always expands this list for Apple Pay.
      const paymentRequest: Record<string, unknown> = {
        countryCode,
        currencyCode,
        supportedNetworks: ['visa', 'masterCard', 'amex', 'discover', 'jcb'],
        merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
        total: {
          label: totalLabel,
          amount,
          type: 'final',
        },
      };
      // No valid checkout email → ask the sheet for it. Apple returns an email
      // only via the SHIPPING contact (`shippingContact.emailAddress`);
      // `requiredBillingContactFields` supports `postalAddress` only per
      // Apple's docs — requesting 'email' there is silently ignored (see
      // adyen-web#3393). An email-only shipping request renders just a
      // "Contact" row, no address section. Skipped when the checkout already
      // has a valid email so the regular sheet stays unchanged.
      if (!hasValidCustomerEmailRef.current) {
        paymentRequest.requiredShippingContactFields = ['email'];
      }
      // Apple Pay JS payment requests accept merchantIdentifier on newer
      // session versions; omit when unset so older Safari stays valid.
      if (activeNuvei.merchantIdentifier) {
        paymentRequest.merchantIdentifier = activeNuvei.merchantIdentifier;
      }
      session = new ApplePaySessionCtor(version, paymentRequest);
      logInfo(
        'Nuvei Apple Pay beginning session',
        {
          countryCode,
          currencyCode,
          amount,
          priceTotal: pricingRef.current.priceBreakdown.priceTotal,
          version,
          hasMerchantIdentifier: !!activeNuvei.merchantIdentifier,
        },
        { apple_pay_begin: '1' }
      );
    } catch (error) {
      logError('Failed to create ApplePaySession for Nuvei', error);
      setErrorRef.current('Apple Pay payment failed');
      finishPayAttempt();
      return;
    }

    const onValidateMerchant = (event: { validationURL: string }) => {
      void (async () => {
        try {
          const topLevelDomain = getGroundTruthTopLevelDomain();
          if (!topLevelDomain) {
            throw new Error('Unable to determine the top-level domain for Apple Pay merchant validation');
          }
          const appleResponse = await fetchNuveiAppleMerchantSession(
            activeProvider.id || '',
            event.validationURL,
            topLevelDomain
          );
          session.completeMerchantValidation(appleResponse);
        } catch (error) {
          logError('Nuvei Apple Pay merchant validation failed', error);
          try {
            session.abort?.();
          } catch {
            /* ignore */
          }
          setErrorRef.current('Apple Pay payment failed');
          finishPayAttempt();
        }
      })();
    };

    const onPaymentAuthorized = (event: {
      payment: {
        token: unknown;
        billingContact?: { emailAddress?: string };
        shippingContact?: { emailAddress?: string };
      };
    }) => {
      notifyPaymentIntentClick(contextRef.current, { paymentMethodName: PaymentMethodEnum.APPLE_PAY });
      setIsExpressPaymentInProcessRef.current(true);
      const walletIssuedEncryptedToken = JSON.stringify(event.payment.token);
      // Sheet email wins, checkout email is the fallback. Shipping contact is
      // the documented carrier for a requested email (Apple never returns one
      // on the billing contact); billing is read defensively.
      const resolvedEmail =
        event.payment.shippingContact?.emailAddress ||
        event.payment.billingContact?.emailAddress ||
        customerRef.current.email ||
        '';
      void (async () => {
        try {
          // No-email click path: the session was not prefetched — create it now
          // with the sheet-collected email (Apple allows async work here; the
          // sheet shows its processing spinner until completePayment).
          let sessionToken = prefetched?.sessionToken;
          if (!sessionToken) {
            // Apple enforces `requiredBillingContactFields` before authorizing
            // — guard anyway: the session gateway hard-rejects an invalid
            // `receipt_email`, and a specific error beats a generic 400 after
            // the shopper already authorized in the sheet.
            if (!isEmailValid(resolvedEmail)) {
              throw new Error('Apple Pay sheet returned no usable email for session creation');
            }
            sessionToken = (
              await createSessionRef.current({
                paymentMethodId: activeProvider.id || '',
                email: resolvedEmail,
              })
            ).sessionToken;
          }
          const params: NuveiApplePayPaymentParams = {
            sessionToken,
            walletIssuedEncryptedToken,
            email: resolvedEmail,
          };
          const result = await runPaymentRef.current(params, PaymentMethodEnum.APPLE_PAY);
          // Only success/pending close the Apple sheet as paid. Redirect still needs
          // a Nuvei step — STATUS_SUCCESS would falsely tell PassKit the charge finished.
          const isCompleted =
            isNuveiSuccessResponse(result, ProviderEnum.NUVEI) || isNuveiPendingResponse(result, ProviderEnum.NUVEI);
          session.completePayment({
            status: isCompleted ? ApplePaySessionCtor.STATUS_SUCCESS : ApplePaySessionCtor.STATUS_FAILURE,
          });
          // runPayment owns success, failure, redirect, and loader handling.
          finishPayAttempt({
            clearExpressLoader: false,
            refreshSession: !isCompleted,
          });
        } catch (error) {
          logError('Nuvei Apple Pay server payment failed', error);
          try {
            session.completePayment({ status: ApplePaySessionCtor.STATUS_FAILURE });
          } catch {
            /* sheet may already be closed */
          }
          setErrorRef.current('Apple Pay payment failed');
          finishPayAttempt();
        }
      })();
    };

    const onCancel = (event?: unknown) => {
      const diagnostics = classifyApplePayCancel(event as { sessionError?: { code?: string; info?: unknown } });
      // Prefer sessionError: Apple sets code "unknown" when PassKit rejects the
      // merchant session (sandbox tester missing, association/cert mismatch).
      // That is NOT a user dismiss — surface it as a payment failure.
      if (isApplePayDomainFailure(diagnostics.classification)) {
        // Pass diagnostics as structured data (not as the Error arg) so the
        // classification, sessionDomain and groundTruthDomain reach RUM instead
        // of being stringified to "[object Object]". The classification is also
        // a label so PassKit rejections are queryable without expanding data.
        logError(
          'Nuvei Apple Pay merchant session rejected by PassKit',
          undefined,
          { ...diagnostics },
          { apple_pay_cancel_classification: diagnostics.classification }
        );
        setErrorRef.current('Apple Pay payment failed');
        // Apple refused this page's merchant session — swap the dead button
        // for the new-tab fallback (no-op outside the framed-phone gate).
        markApplePayDomainFailure();
      }
      finishPayAttempt();
    };

    session.onvalidatemerchant = onValidateMerchant;
    session.onpaymentauthorized = onPaymentAuthorized;
    session.oncancel = onCancel;

    // begin() must stay on the click stack for the Apple user-gesture requirement.
    try {
      session.begin();
    } catch (error) {
      logError('Failed to begin ApplePaySession for Nuvei', error);
      setErrorRef.current('Apple Pay payment failed');
      finishPayAttempt();
    }
  }, [
    addressDetailsRef,
    contextRef,
    createSessionRef,
    customerRef,
    finishPayAttempt,
    hasValidCustomerEmailRef,
    isPayingRef,
    nuveiConfigRef,
    onInvalidTaxClickRef,
    prefetchRef,
    pricingRef,
    providerSettingsRef,
    refreshPaymentReadiness,
    runPaymentRef,
    sessionBindingKeyRef,
    setErrorRef,
    setIsExpressPaymentInProcessRef,
    setState,
    settleAvailability,
  ]);

  // No valid checkout email → the session prefetch is deferred, but the button
  // must stay CLICKABLE (Adyen/Stripe parity): the sheet collects the email and
  // the session is created in `onpaymentauthorized`. With a valid email, keep
  // the prefetch-freshness gate so a click always has a session ready.
  const isButtonInteractive = isPaymentReady || !hasValidCustomerEmail;

  if (state === 'unavailable') {
    return <PaymentMethodNotSupported isExpress={isExpress} />;
  }

  return (
    <WalletButtonShell
      state={buttonState}
      placeholder={<ApplePayButtonPlaceholder />}
      testId="wallet-btn-apple-nuvei"
      usePlaceholderAsVisual
    >
      <button
        type="button"
        className="w-full h-12 rounded-lg bg-black text-white font-medium"
        data-testid="nuvei-apple-pay-button"
        onClick={onPay}
        disabled={!isButtonInteractive}
      >
        Apple Pay
      </button>
    </WalletButtonShell>
  );
};
